import { Resolver } from 'react-hook-form';
import { array, option } from 'fp-ts';
import { pipe } from 'fp-ts/function';
import {
    SmsUsaRegistration,
    SmsUsaRegistrationBusinessIndustry,
    SmsUsaRegistrationBusinessRegionOfOperation,
    SmsUsaRegistrationBusinessRegistrationIdentifier,
    SmsUsaRegistrationBusinessType,
    SmsUsaRegistrationJobPosition,
} from '@mews-commander/data-access';
import { createFormResolver } from '@mews-commander/form';
import { Form } from '@mews/framework';
import { useCommanderSelector } from '@mews-commander/utils';
import { validation as validate } from '@mews-commander/legacy-form';
import { getCountrySubdivisionOptions } from '@mews-commander/legacy-entities/src/ducks/countrySubdivisions';
import { BrandRegistrationDialogView } from './BrandRegistrationDialogView';
import { BrandRegistrationFormValues } from './types';
import { getConstraints } from './constraints';
import { getDefaultValues } from './defaultValues';
import { useSmsUsaBrandRegistrationAdd } from './queries';

interface BrandRegistrationDialogFormProps {
    active: boolean;
    addBrandRegistrationMutationResult: ReturnType<typeof useSmsUsaBrandRegistrationAdd>;
    enterpriseId: string;
    onClose: () => void;
    smsUsaRegistration: SmsUsaRegistration;
}

export const BrandRegistrationDialogForm = ({
    active,
    addBrandRegistrationMutationResult,
    enterpriseId,
    onClose,
    smsUsaRegistration,
}: BrandRegistrationDialogFormProps) => {
    const { isPending, mutate } = addBrandRegistrationMutationResult;

    const handleSubmit = (values: BrandRegistrationFormValues) => {
        const {
            firstName,
            lastName,
            jobTitle,
            phoneNumber,
            websiteUrl,
            businessName,
            businessRegistrationNumber,
            city,
            postalCode,
            countryCode,
            addressLine1,
            addressLine2,
            countrySubdivisionCode,
            email,
        } = values;

        const jobPosition = findEnum(SmsUsaRegistrationJobPosition, values.jobPosition);
        const businessIndustry = findEnum(SmsUsaRegistrationBusinessIndustry, values.businessIndustry);
        const businessRegistrationIdentifier = findEnum(
            SmsUsaRegistrationBusinessRegistrationIdentifier,
            values.businessRegistrationIdentifier
        );
        const businessType = findEnum(SmsUsaRegistrationBusinessType, values.businessType);

        const businessRegionsOfOperation = pipe(
            values.businessRegionsOfOperation,
            array.filterMap((value) => {
                const nullableEnum = findEnum(SmsUsaRegistrationBusinessRegionOfOperation, value);

                /*
                 * We cannot use fromNullable because it gets confused.
                 * It returns Option<NonNullable> instead of converting the undefined to Option.
                 */
                return nullableEnum != null ? option.some(nullableEnum) : option.none;
            })
        );

        if (
            jobPosition == null ||
            businessIndustry == null ||
            businessRegistrationIdentifier == null ||
            businessType == null
        ) {
            throw new Error(
                `Invalid input: one or more of the required fields are nil. ${JSON.stringify({
                    businessIndustry,
                    businessRegistrationIdentifier,
                    businessType,
                    jobPosition,
                })}`
            );
        }

        mutate({
            enterpriseId,
            businessContact: {
                firstName,
                lastName,
                email,
                jobPosition,
                jobTitle,
                phoneNumber,
            },
            customerProfile: {
                businessIndustry,
                businessName,
                businessRegionsOfOperation,
                businessRegistrationIdentifier,
                businessRegistrationNumber,
                businessType,
                city,
                countryCode,
                subdivisionCode: countrySubdivisionCode,
                postalCode,
                street: addressLine1,
                streetSecondary: addressLine2,
                websiteUrl,
            },
        });
    };

    const hasCountrySubdivisions = useCommanderSelector((state) => (countryCode: string) => {
        const subdivisions = getCountrySubdivisionOptions(state, { countryCode });
        return array.isNonEmpty(subdivisions);
    });

    const resolver: Resolver<BrandRegistrationFormValues, unknown> = async (values, context, options) => {
        const constraints = getConstraints({ hasCountrySubdivisions, values });

        const formResolver = createFormResolver<BrandRegistrationFormValues>(validate(constraints));

        return formResolver(values, context, options);
    };

    const defaultValues = getDefaultValues(smsUsaRegistration.defaultValues);

    return (
        <Form onSubmit={handleSubmit} resolver={resolver} mode="onTouched" defaultValues={defaultValues}>
            {({ submit }) => (
                <BrandRegistrationDialogView
                    active={active}
                    isLoading={isPending}
                    onCancel={onClose}
                    smsUsaRegistration={smsUsaRegistration}
                    submit={submit}
                />
            )}
        </Form>
    );
};

const findEnum = <T extends object>(enumType: T, stringValue: string): T[keyof T] | undefined =>
    Object.values(enumType).find((enumValue) => enumValue === stringValue);
